import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';

import {
  IconAlertCircle,
  IconCheck,
  IconLock,
  IconLoader2,
  IconSailboat,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import type { BoatCreationSetup } from '../../services/boats';
import '../../styles/BoatCreationModal.css';

const BOAT_NAME_MAX_LENGTH = 255;

interface BoatCreationModalProps {
  open: boolean;
  setup: BoatCreationSetup | null;
  isSubmitting?: boolean;
  error?: string;
  onCreate: (boatName: string) => Promise<void> | void;
}

export const BoatCreationModal: FC<BoatCreationModalProps> = ({
  open,
  setup,
  isSubmitting = false,
  error = '',
  onCreate,
}) => {
  const { t } = useTranslation('common');
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const [boatName, setBoatName] = useState('');
  const [localError, setLocalError] = useState('');

  const lockedBoatType = setup?.boatTypeName ?? setup?.planBoatName ?? t('boats.create.pendingType', 'Pendiente');
  const blockedReason = useMemo(() => {
    if (!setup || setup.canCreateBoat) {
      return '';
    }
    switch (setup.reason) {
      case 'missing-plan': {
        return t('boats.create.missingPlan', 'Activa un plan antes de crear tu barco.');
      }
      case 'already-created': {
        return t('boats.create.alreadyCreated', 'Esta cuenta ya tiene un barco creado.');
      }
      case 'admin-required': {
        return t(
          'boats.create.adminRequired',
          'Un admin debe crear el barco para esta cuenta unlimited.',
        );
      }
      case 'boat-type-unavailable': {
        return t(
          'boats.create.boatTypeUnavailable',
          'No encontramos el tipo de barco asociado a este plan.',
        );
      }
      default: {
        return '';
      }
    }
  }, [setup, t]);
  const visibleError = localError || error || blockedReason;
  const trimmedName = boatName.trim();
  const canSubmit = Boolean(setup?.canCreateBoat) && trimmedName.length > 0 && !isSubmitting;

  const helperText = useMemo(() => {
    if (!setup) {
      return t('boats.create.loading', 'Comprobando el plan y los barcos de tu cuenta.');
    }
    if (setup.canCreateBoat) {
      return t(
        'boats.create.nameHint',
        'Puedes cambiar el nombre despues en Settings. El tipo queda fijado por tu plan.',
      );
    }
    return t(
      'boats.create.blockedHint',
      'No se puede crear el barco desde esta cuenta. Revisa el plan o pide ayuda a un admin.',
    );
  }, [setup, t]);

  useEffect(() => {
    if (open) {
      window.setTimeout(() => nameInputRef.current?.focus(), 0);
    } else {
      setBoatName('');
      setLocalError('');
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      const previousBodyOverflow = document.body.style.overflow;
      const previousHtmlOverflow = document.documentElement.style.overflow;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previousBodyOverflow;
        document.documentElement.style.overflow = previousHtmlOverflow;
      };
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLocalError('');
    if (!setup?.canCreateBoat) {
      setLocalError(t('boats.create.blockedError', 'Este barco debe crearlo un admin.'));
      return;
    }
    if (trimmedName.length === 0) {
      setLocalError(t('boats.create.nameRequired', 'El nombre del barco es obligatorio.'));
      return;
    }
    if (trimmedName.length > BOAT_NAME_MAX_LENGTH) {
      setLocalError(
        t('boats.create.nameTooLong', 'El nombre del barco debe tener {{count}} caracteres o menos.', {
          count: BOAT_NAME_MAX_LENGTH,
        }),
      );
      return;
    }
    await onCreate(trimmedName);
  };

  return (
    <div className="boat-create-overlay" role="presentation">
      <section
        className="boat-create-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="boat-create-title"
        aria-describedby="boat-create-description"
      >
        <div className="boat-create-header">
          <div className="boat-create-title-lockup">
            <span className="boat-create-mark" aria-hidden="true">
              <IconSailboat size={18} stroke={1.8} />
            </span>
            <div>
              <h2 id="boat-create-title">{t('boats.create.title', 'Configura tu barco')}</h2>
              <p id="boat-create-description">
                {t(
                  'boats.create.description',
                  'Usaremos este barco para analisis y reportes.',
                )}
              </p>
            </div>
          </div>
        </div>

        <form className="boat-create-body" onSubmit={handleSubmit}>
          <div className="boat-create-form">
            <label className="boat-create-field" htmlFor="boat-create-name">
              <span>{t('boats.create.nameLabel', 'Nombre del barco')}</span>
              <input
                ref={nameInputRef}
                id="boat-create-name"
                value={boatName}
                onChange={(event) => setBoatName(event.currentTarget.value)}
                maxLength={BOAT_NAME_MAX_LENGTH}
                disabled={!setup?.canCreateBoat || isSubmitting}
                aria-describedby="boat-create-name-help"
                autoComplete="off"
              />
            </label>
            <p id="boat-create-name-help" className="boat-create-help">
              {helperText}
            </p>

            {visibleError && (
              <p className="boat-create-error" role="alert">
                <IconAlertCircle size={17} stroke={1.9} aria-hidden="true" />
                <span>{visibleError}</span>
              </p>
            )}
          </div>

          <aside className="boat-create-summary" aria-label={t('boats.create.summary', 'Resumen del barco')}>
            <div className="boat-create-type" role="group" aria-label={t('boats.create.typeLabel', 'Tipo de barco')}>
              <span>
                <IconLock size={16} stroke={1.9} aria-hidden="true" />
                {t('boats.create.typeLabel', 'Tipo de barco')}
              </span>
              <strong>{lockedBoatType}</strong>
              <small>{t('boats.create.typeLocked', 'Bloqueado por el plan')}</small>
            </div>

            <div className="boat-create-included">
              <span>
                <IconCheck size={16} stroke={2} aria-hidden="true" />
                {t('boats.create.includedOne', '1 barco incluido')}
              </span>
              <span>
                <IconCheck size={16} stroke={2} aria-hidden="true" />
                {t('boats.create.defaultBoat', 'Se guardara como predeterminado')}
              </span>
            </div>

            <button type="submit" className="boat-create-primary" disabled={!canSubmit}>
              {isSubmitting && <IconLoader2 size={17} aria-hidden="true" />}
              {isSubmitting
                ? t('boats.create.creating', 'Creando...')
                : t('boats.create.submit', 'Crear barco')}
            </button>
          </aside>
        </form>
      </section>
    </div>
  );
};
